<template>
  <div class="relative">
    <VButton
      data-intercom-target="create-slide-button"
      variant="copper-borderless"
      :class="buttonClass"
      no-shadow="noShadow"
      :disabled="disabled"
      @click="showCreateSlideList = !showCreateSlideList"
    >
      <PlusIcon class="w-8" /><span class="uppercase xl1_hidden">Add</span><span class="hidden xl1_inline">Add Slide</span>
    </VButton>
    <FloatingMenu v-if="showCreateSlideList" class="floating-slide-list" arrow-position="top">
      <div ref="slideListRef" class="slide-list">
        <VButton class="slide-list-button" variant="copper-borderless" no-shadow="noShadow" @click="onCreateSlide('P')">
          <DocumentTextIcon class="w-6" /><span>PDF</span>
        </VButton>
        <VButton class="slide-list-button" variant="copper-borderless" no-shadow="noShadow" @click="onCreateSlide('I')">
          <PhotoIcon class="w-6" /><span>Image</span>
        </VButton>
        <VButton class="slide-list-button" variant="copper-borderless" no-shadow="noShadow" @click="onCreateSlide('M')">
          <ThreeDIcon /><span>3D</span>
        </VButton>
        <VButton class="slide-list-button" variant="copper-borderless" no-shadow="noShadow" @click="onCreateSlide('S')">
          <HotspotIcon /><span>360</span>
        </VButton>
        <VButton class="slide-list-button" variant="copper-borderless" no-shadow="noShadow" @click="onCreateSlide('V')">
          <FilmIcon class="w-6" /><span>Video</span>
        </VButton>
        <VButton
          v-if="hasRole('internal_admin')"
          class="slide-list-button bronze"
          variant="bronze-borderless"
          no-shadow="noShadow"
          @click="onCreateSlide('D')"
        >
          <CodeBracketIcon class="w-6" /><span>URL</span>
        </VButton>
        <VButton
          class="slide-list-button"
          variant="copper-borderless"
          no-shadow="noShadow"
          @click=";(showCreateSlideList = false), $emit('import-slides')"
        >
          <FontAwesomeIcon :style="{ height: '1rem', width: '1rem' }" icon="file-import" class="w-4 h-4" size="sm" />
          <span>Import from another 3D Presentation</span>
        </VButton>
      </div>
    </FloatingMenu>
  </div>
</template>

<script lang="ts" setup>
import type { SlideType } from '@/entities/deck'
import { CodeBracketIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { DocumentTextIcon, PhotoIcon } from '@heroicons/vue/24/solid'
import { FilmIcon } from '@heroicons/vue/24/outline'
import ThreeDIcon from '@/components/icons/ThreeDSlideIcon.svg'
import HotspotIcon from '@/components/icons/site/hotspot-360.svg'

defineProps({
  buttonClass: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['create-slide', 'import-slides'])

const { hasRole } = useUser()
const showCreateSlideList = ref(false)
const slideListRef = ref<HTMLElement | null>(null)

const onDocumentClick = (e: MouseEvent): void => {
  if (!showCreateSlideList.value) {
    return
  }

  if (e.target !== slideListRef.value && !slideListRef.value?.contains(e.target as any)) {
    showCreateSlideList.value = false
  }
}

const onCreateSlide = (slideType: SlideType): void => {
  showCreateSlideList.value = false

  emit('create-slide', { type: slideType })
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocumentClick)
})
</script>

<style lang="stylus" scoped>
.floating-slide-list
  @apply absolute z-20 left-1
  top: calc(100% + 0.75rem)
  --arrowLeft: 2rem

  .slide-list
    @apply w-64 rounded-2xl overflow-hidden divide-y divide-gray-300

    .slide-list-button
      @apply w-full flex gap-2 px-6 py-3 cursor-pointer rounded-none

      svg
        @apply w-5 h-5 text-copper-600

      span
        @apply text-sm leading-5 font-normal

      &.bronze
        svg
          @apply w-5 h-5 text-bronze-600
</style>
